import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { ConfigProvider } from 'antd';
import zhCN from 'antd/locale/zh_CN';
import MainLayout from './components/Layout/MainLayout';
import Login from './pages/Login';
import DeviceModelManagement from './pages/DeviceModelManagement';
import DeviceManagement from './pages/DeviceManagement';
import VersionManagement from './pages/VersionManagement';
import TaskManagement from './pages/TaskManagement';
import AppUpgradeManagement from './pages/AppUpgradeManagement';
import UpgradeStatistics from './pages/UpgradeStatistics';
import DeviceStatistics from './pages/DeviceStatistics';
import VersionStatistics from './pages/VersionStatistics';
import UserManagement from './pages/UserManagement';
import SystemLog from './pages/SystemLog';
import AccountManagement from './pages/AccountManagement';
import { DrawerProvider } from '@/contexts/DrawerContext';
import { AuthProvider } from '@/contexts/AuthContext';
import { PermissionProvider, usePermission } from '@/contexts/PermissionContext';
import 'antd/dist/reset.css';

// 认证保护组件
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const isLoggedIn = localStorage.getItem('isLoggedIn') === 'true';
  
  if (!isLoggedIn) {
    return <Navigate to="/login" replace />;
  }
  
  return <>{children}</>;
};

// 权限保护组件
const PermissionRoute: React.FC<{
  children: React.ReactNode;
  requiredPermission?: string;
}> = ({ children, requiredPermission }) => {
  const location = useLocation();
  const { canAccessRoute, hasPermission, loading } = usePermission();

  // 如果权限数据还在加载中，显示加载占位符（保持布局不变）
  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100%',
        minHeight: '200px'
      }}>
        <div style={{ color: '#999' }}>加载中...</div>
      </div>
    );
  }

  if (requiredPermission) {
    // 如果指定了权限，检查该权限
    if (!hasPermission(requiredPermission as any)) {
      return <Navigate to="/device-model" replace />;
    }
  } else {
    // 如果没有指定权限，根据路由检查
    if (!canAccessRoute(location.pathname)) {
      return <Navigate to="/device-model" replace />;
    }
  }

  return <>{children}</>;
};

function App() {
  return (
    <ConfigProvider
      locale={zhCN}
      theme={{
        token: {
          colorPrimary: '#1677ff',
          colorBgBase: '#ffffff',
          colorTextBase: '#000000',
          colorBgContainer: '#ffffff',
          colorBgElevated: '#ffffff',
          colorBorder: '#e8e8e8',
          borderRadius: 6,
        },
        components: {
          Button: {
            primaryColor: '#ffffff',
          },
          Table: {
            headerBg: '#fafafa',
            headerColor: '#000000',
            borderColor: '#e8e8e8',
          },
          Card: {
            headerBg: '#fafafa',
          },
          Input: {
            colorBgContainer: '#ffffff',
            colorBorder: '#e8e8e8',
          },
          Select: {
            colorBgContainer: '#ffffff',
            colorBorder: '#e8e8e8',
          },
        },
      }}
    >
    <BrowserRouter>
      <AuthProvider>
        <PermissionProvider>
          <DrawerProvider>
            <Routes>
          {/* 登录页面 - 不需要布局 */}
          <Route path="/login" element={<Login />} />
          
          {/* 受保护的路由 - 需要登录 */}
          <Route path="/" element={
            <ProtectedRoute>
              <MainLayout>
                <Navigate to="/device-model" replace />
              </MainLayout>
            </ProtectedRoute>
          } />
          
          <Route path="/device-model" element={
            <ProtectedRoute>
              <PermissionRoute requiredPermission="device:view">
                <MainLayout>
                  <DeviceModelManagement />
                </MainLayout>
              </PermissionRoute>
            </ProtectedRoute>
          } />
          
          <Route path="/device" element={
            <ProtectedRoute>
              <PermissionRoute requiredPermission="device:view">
                <MainLayout>
                  <DeviceManagement />
                </MainLayout>
              </PermissionRoute>
            </ProtectedRoute>
          } />
          
          <Route path="/version" element={
            <ProtectedRoute>
              <PermissionRoute requiredPermission="version:view">
                <MainLayout>
                  <VersionManagement />
                </MainLayout>
              </PermissionRoute>
            </ProtectedRoute>
          } />
          
          <Route path="/task" element={
            <ProtectedRoute>
              <PermissionRoute requiredPermission="progress:monitor">
                <MainLayout>
                  <TaskManagement />
                </MainLayout>
              </PermissionRoute>
            </ProtectedRoute>
          } />
          
          <Route path="/app-upgrade" element={
            <ProtectedRoute>
              <PermissionRoute requiredPermission="version:view">
                <MainLayout>
                  <AppUpgradeManagement />
                </MainLayout>
              </PermissionRoute>
            </ProtectedRoute>
          } />
          
          <Route path="/upgrade-stats" element={
            <ProtectedRoute>
              <PermissionRoute requiredPermission="progress:monitor">
                <MainLayout>
                  <UpgradeStatistics />
                </MainLayout>
              </PermissionRoute>
            </ProtectedRoute>
          } />
          
          <Route path="/device-stats" element={
            <ProtectedRoute>
              <PermissionRoute requiredPermission="progress:monitor">
                <MainLayout>
                  <DeviceStatistics />
                </MainLayout>
              </PermissionRoute>
            </ProtectedRoute>
          } />
          
          <Route path="/version-stats" element={
            <ProtectedRoute>
              <PermissionRoute requiredPermission="progress:monitor">
                <MainLayout>
                  <VersionStatistics />
                </MainLayout>
              </PermissionRoute>
            </ProtectedRoute>
          } />
          
          <Route path="/user" element={
            <ProtectedRoute>
              <PermissionRoute requiredPermission="user:manage">
                <MainLayout>
                  <UserManagement />
                </MainLayout>
              </PermissionRoute>
            </ProtectedRoute>
          } />
          
          <Route path="/system-log" element={
            <ProtectedRoute>
              <PermissionRoute requiredPermission="audit:view">
                <MainLayout>
                  <SystemLog />
                </MainLayout>
              </PermissionRoute>
            </ProtectedRoute>
          } />
          
          <Route path="/account" element={
            <ProtectedRoute>
              <MainLayout>
                <AccountManagement />
              </MainLayout>
            </ProtectedRoute>
          } />
          
          {/* 404 重定向到登录 */}
          <Route path="*" element={<Navigate to="/login" replace />} />
          </Routes>
          </DrawerProvider>
        </PermissionProvider>
      </AuthProvider>
    </BrowserRouter>
    </ConfigProvider>
  );
}

export default App;